import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import DetailsDescriptionSkeleton from '../../../components/Skeletons/ProductOverview/Details/DetailsDescriptionSkeleton';
import DetailsImageSkeleton from '../../../components/Skeletons/ProductOverview/Details/DetailsImageSkeleton';
import DetailsSkeleton from '../../../components/Skeletons/ProductOverview/Details/DetailsSkeleton';
import dataPlaceholders from '../../../constants/dataPlaceholders';
import useNotifications from '../../../hooks/useNotifications';
import productImageService from '../../../services/productImageService';
import productService from '../../../services/productService';

import './DetailTab.scss';

const DetailTab: FC = () => {
  const intl = useIntl();
  const { notify } = useNotifications();
  const title = intl.formatMessage({ id: 'productsOverview.detailTab.productDetails.title' });
  const { id: productId } = useParams<'id'>();
  const { data: details, isLoading: isLoadingDetails } = useQuery(
    ['productDetails', productId],
    () => productService.get(productId!),
    {
      onError: () =>
        notify(
          'error',
          title,
          intl.formatMessage({ id: 'productsOverview.detailTab.notification.unableLoadProductDetails' }),
        ),
    },
  );

  return (
    <div className="details-tab-component pb-2">
      <div className="row gx-4 mb-4">
        <div className="col-9">
          {isLoadingDetails ? (
            <DetailsSkeleton />
          ) : (
            <div className="animated-component-item row mb-4">
              <div className="card">
                <div className="card-divider font-bold text-uppercase p-3">
                  <FormattedMessage id="productsOverview.detailTab.productDetails.title" />
                </div>
                <div className="px-3 py-4">
                  <div className="row">
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.tradeName" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-name">
                        {details?.name || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.commercialName" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-commercialName">
                        {details?.commercialName || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.casNumber" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-casNumber">
                        {details?.casNumber || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.productType" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-productType">
                        {details?.productType || dataPlaceholders.missingData}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.molecularWeight" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-molecularWeight">
                        {details?.molecularWeight || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.originator" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-originator">
                        {details?.originator || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.approvedFDA" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-yearApprovedFda">
                        {details?.yearApprovedFda || dataPlaceholders.missingData}
                      </div>
                    </div>
                    <div className="col-3 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.approvedEMA" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-yearApprovedEma">
                        {details?.yearApprovedEma || dataPlaceholders.missingData}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-8 pb-3">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.iupacName" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-iupacName">
                        {details?.iupacName || dataPlaceholders.missingData}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-8">
                      <div className="size-caption text-color-gray-l6 pb-2">
                        <FormattedMessage id="productsOverview.detailTab.productDetails.smiles" />
                      </div>
                      <div className="size-label" data-test-id="productsOverview-details-smiles">
                        {details?.smiles || dataPlaceholders.missingData}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="row">
            <div className="card">
              <div className="card-divider font-bold text-uppercase p-3">
                <FormattedMessage id="productsOverview.detailTab.description.title" />
              </div>
              <div className="col px-3 py-4">
                {isLoadingDetails && <DetailsDescriptionSkeleton />}
                <div
                  className="animated-component-item description"
                  data-test-id="productsOverview-details-description"
                >
                  {details?.description || dataPlaceholders.missingData}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-3">
          {isLoadingDetails ? (
            <DetailsImageSkeleton />
          ) : (
            <div className="animated-component-item card p-3">
              <div className="font-bold text-uppercase pb-3">
                <FormattedMessage id="productsOverview.detailTab.chemicalStructure.title" />
              </div>
              <div className="text-center">
                <img
                  src={productImageService.getImageUrl(details?.imageId)}
                  alt={details?.name}
                  data-test-id="productsOverview-details-image"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailTab;
